<template>
    <div class="configurator">
        <component
                v-if="stepComponent && currentStepState"
                :is="stepComponent"
                v-bind="currentStepState"
        />

        <nav
                class="bottom-step-nav"
                v-if="navigationVisible"
        >
            <v-button
                    class="btn-main--white bottom-step-nav__button bottom-step-nav__button--prev"
                    v-if="prevButton.visible"
                    :left-arrow="prevButton.arrow"
                    @click="handlePrev"
            >
                {{ prevButton.text }}
            </v-button>

            <v-button
                    class="btn-main--white bottom-step-nav__button bottom-step-nav__button--cancel"
                    v-if="editing"
                    @click="handleCancel"
            >
                {{ $t('cancel') }}
            </v-button>

            <v-button
                    class="bottom-step-nav__button bottom-step-nav__right-button"
                    :right-arrow="nextButton.arrow"
                    :disabled="nextButton.disabled"
                    @click="handleNext"
            >
                <span class="mobile">{{ nextButton.mobileText }}</span>
                <span class="desktop">{{ nextButton.text }}</span>
            </v-button>
        </nav>
    </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import FilesStep from '@/components/steps/Files.vue'
  import StyleStep from '@/components/steps/Style.vue'
  import MaterialStep from '@/components/steps/Material.vue'
  import ProfileStep from '@/components/steps/Profile.vue'
  import WindowOpeningType from '@/components/steps/WindowOpeningType.vue'
  import WindowType from '@/components/steps/WindowType.vue'
  import Safety from '@/components/steps/Safety.vue'
  import ColorInner from '@/components/steps/ColorInner.vue'
  import ColorOuter from '@/components/steps/ColorOuter.vue'
  import SizeAndQuantity from '@/components/steps/SizeAndQuantity.vue'
  import Innovations from '@/components/steps/Innovations.vue'
  import ConfigurationName from '@/components/steps/ConfigurationName.vue'
  import Summary from '@/components/steps/Summary.vue'

  const STEP_COMPONENT_MAP = {
    files: FilesStep,
    style: StyleStep,
    material: MaterialStep,
    profile: ProfileStep,
    windowOpeningType: WindowOpeningType,
    windowType: WindowType,
    safety: Safety,
    colorInner: ColorInner,
    colorOuter: ColorOuter,
    innovations: Innovations,
    sizeAndQuantity: SizeAndQuantity,
    configurationName: ConfigurationName,
    summary: Summary,
  }

  export default {
    name: 'Configurator',
    props: {
      type: {
        type: String,
        required: true,
      },
      editId: String,
    },
    beforeRouteLeave (to, from, next) {
      const { $store, editing } = this

      if (!editing) {
        next()
        return
      }

      $store.commit('openConfirmModal', {
        message: 'youAreInConfigurationEditMode',
        onConfirm: () => {
          $store.commit('resetConfirmModal')
          next()
        },
      })
    },
    mounted () {
      const { editId, type, $store } = this

      if (!editId) {
        $store.dispatch('configurator/open', { type })
      } else {
        $store.dispatch('configurator/edit', { type, configurationId: editId })
      }
    },
    methods: {
      handleCancel () {
        this.$router.push({ name: 'home' })
      },
      async handleSave () {
        await this.$store.dispatch('configurator/save')
      },
      async handleNext () {
        await this.$store.dispatch('configurator/goToNextStep')
      },
      async handlePrev () {
        const { $store, $router, isFirstStep, editing } = this

        if (isFirstStep || (editing && isFirstStep)) {
          $router.push({ name: 'home' })
          return
        }

        await $store.dispatch('configurator/goToPreviousStep')
      },
    },
    computed: {
      ...mapState({
        currentStep: state => state.configurator.currentStep,
        files: state => state.configurator.files,
        configurations: state => state.configurations,
      }),
      ...mapGetters({
        currentStepState: 'configurator/currentStepState',
        hasPreviousStep: 'configurator/hasPreviousStep',
        editing: 'configurator/editing',
        creating: 'configurator/creating',
        stepNumber: 'configurator/stepNumber',
        isFormValid: 'configurator/isFormValid',
        isLastStep: 'configurator/isLastStep',
        isFirstStep: 'configurator/isFirstStep',
      }),
      stepComponent () {
        const { currentStep } = this

        if (!currentStep.id) {
          return null
        }

        if (!(currentStep.id in STEP_COMPONENT_MAP)) {
          throw new Error(`Could not find component for step ${currentStep.id}`)
        }

        return STEP_COMPONENT_MAP[currentStep.id]
      },
      navigationVisible () {
        const { currentStep } = this
        return currentStep && currentStep.id !== 'summary'
      },
      showPrevButton () {
        const { configurations, stepNumber, files } = this

        if (stepNumber > 1) {
          return true
        }

        if (configurations.length > 0 || files.length > 0) {
          return false
        }

        return true
      },
      canSave () {
        return this.isFormValid
      },
      canGoToNextStep () {
        const { currentStepState } = this
        return currentStepState && !currentStepState.valid$
      },
      prevButton () {
        const { isFirstStep, editing, configurations, creating, $t } = this
        const result = { visible: true, arrow: true, text: $t('back') }

        if (creating && configurations.length > 0 && isFirstStep) {
          return { ...result, visible: false }
        }

        if (editing && isFirstStep) {
          return { ...result, visible: false }
        }

        return result
      },
      nextButton () {
        const { isLastStep, isFormValid, editing, currentStepState, $t } = this
        const result = {
          arrow: true,
          text: $t('nextStep'),
          mobileText: $t('next'),
          disabled:
            (isLastStep && !isFormValid) ||
            (currentStepState && !currentStepState.valid$),
        }

        if (isLastStep && editing) {
          return { ...result, text: $t('save'), mobileText: $t('save') }
        }

        return result
      },
    },
    watch: {
      configurations (items, oldItems) {

        if (this.$store.state.configurationSent == false) {
          if (oldItems.length > 0 && items.length === 0) {
            this.$router.push({ name: 'home' })
          }
        } else {
          this.$store.commit('setConfigurationSent', false)
          this.$router.push({ name: 'thanks' })
        }

      },
    },
    beforeDestroy () {
      const { $store } = this
      $store.dispatch('configurator/close')
    },
  }


</script>
